import { __rest } from "tslib";
import * as React from 'react';
import VirtualList from 'rc-virtual-list';
import MotionTreeNode from './MotionTreeNode';
import { findExpandedKeys, getExpandRange } from './utils/diffUtil';
import { getTreeNodeProps, getKey } from './utils/treeUtil';
const HIDDEN_STYLE = {
    width: 0,
    height: 0,
    display: 'flex',
    overflow: 'hidden',
    opacity: 0,
    border: 0,
    padding: 0,
    margin: 0,
};
const noop = () => { };
export const MOTION_KEY = `RC_TREE_MOTION_${Math.random()}`;
const MotionNode = {
    key: MOTION_KEY,
};
export const MotionEntity = {
    key: MOTION_KEY,
    level: 0,
    index: 0,
    pos: '0',
    node: MotionNode,
};
const MotionFlattenData = {
    parent: null,
    children: [],
    pos: MotionEntity.pos,
    data: MotionNode,
    title: null,
    key: MOTION_KEY,
    isStart: [],
    isEnd: [],
};
export function getMinimumRangeTransitionRange(list, virtual, height, itemHeight) {
    if (virtual === false || !height) {
        return list;
    }
    return list.slice(0, Math.ceil(height / itemHeight) + 1);
}
function itemKey(item) {
    const { data: { key }, pos, } = item;
    return getKey(key, pos);
}
function getAccessibilityPath(item) {
    let path = String(item.data.key);
    let current = item;
    while (current.parent) {
        current = current.parent;
        path = `${current.data.key} > ${path}`;
    }
    return path;
}
const RefNodeList = (props, ref) => {
    const { prefixCls, data, selectable, checkable, expandedKeys, selectedKeys, checkedKeys, loadedKeys, loadingKeys, halfCheckedKeys, keyEntities, disabled, dragging, dragOverNodeKey, dropPosition, motion, height, itemHeight, virtual, focusable, activeItem, focused, tabIndex, onKeyDown, onFocus, onBlur, onActiveChange, onListChangeStart, onListChangeEnd } = props, domProps = __rest(props, ["prefixCls", "data", "selectable", "checkable", "expandedKeys", "selectedKeys", "checkedKeys", "loadedKeys", "loadingKeys", "halfCheckedKeys", "keyEntities", "disabled", "dragging", "dragOverNodeKey", "dropPosition", "motion", "height", "itemHeight", "virtual", "focusable", "activeItem", "focused", "tabIndex", "onKeyDown", "onFocus", "onBlur", "onActiveChange", "onListChangeStart", "onListChangeEnd"]);
    const listRef = React.useRef(null);
    const indentMeasurerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => ({
        scrollTo: scroll => {
            listRef.current.scrollTo(scroll);
        },
        getIndentWidth: () => indentMeasurerRef.current.offsetWidth,
    }));
    const [prevExpandedKeys, setPrevExpandedKeys] = React.useState(expandedKeys);
    const [prevData, setPrevData] = React.useState(data);
    const [transitionData, setTransitionData] = React.useState(data);
    const [transitionRange, setTransitionRange] = React.useState([]);
    const [motionType, setMotionType] = React.useState(null);
    function onMotionEnd() {
        setPrevData(data);
        setTransitionData(data);
        setTransitionRange([]);
        setMotionType(null);
        onListChangeEnd();
    }
    React.useEffect(() => {
        setPrevExpandedKeys(expandedKeys);
        const diffExpanded = findExpandedKeys(prevExpandedKeys, expandedKeys);
        if (diffExpanded.key !== null) {
            if (diffExpanded.add) {
                const keyIndex = prevData.findIndex(({ data: { key } }) => key === diffExpanded.key);
                const rangeNodes = getMinimumRangeTransitionRange(getExpandRange(prevData, data, diffExpanded.key), virtual, height, itemHeight);
                const newTransitionData = prevData.slice();
                newTransitionData.splice(keyIndex + 1, 0, MotionFlattenData);
                setTransitionData(newTransitionData);
                setTransitionRange(rangeNodes);
                setMotionType('show');
            }
            else {
                const keyIndex = data.findIndex(({ data: { key } }) => key === diffExpanded.key);
                const rangeNodes = getMinimumRangeTransitionRange(getExpandRange(data, prevData, diffExpanded.key), virtual, height, itemHeight);
                const newTransitionData = data.slice();
                newTransitionData.splice(keyIndex + 1, 0, MotionFlattenData);
                setTransitionData(newTransitionData);
                setTransitionRange(rangeNodes);
                setMotionType('hide');
            }
        }
        else if (prevData !== data) {
            setPrevData(data);
            setTransitionData(data);
        }
    }, [expandedKeys, data]);
    React.useEffect(() => {
        if (!dragging) {
            onMotionEnd();
        }
    }, [dragging]);
    const mergedData = motion ? transitionData : data;
    const treeNodeRequiredProps = {
        expandedKeys,
        selectedKeys,
        loadedKeys,
        loadingKeys,
        checkedKeys,
        halfCheckedKeys,
        dragOverNodeKey,
        dropPosition,
        keyEntities,
    };
    return (React.createElement(React.Fragment, null,
        focused && activeItem && (React.createElement("span", { style: HIDDEN_STYLE, "aria-live": "assertive" }, getAccessibilityPath(activeItem))),
        React.createElement("div", null,
            React.createElement("input", { style: HIDDEN_STYLE, disabled: focusable === false || disabled, tabIndex: focusable !== false ? tabIndex : null, onKeyDown: onKeyDown, onFocus: onFocus, onBlur: onBlur, value: "", onChange: noop, "aria-label": "for screen reader" })),
        React.createElement("div", { className: `${prefixCls}-treenode`, "aria-hidden": true, style: {
                position: 'absolute',
                pointerEvents: 'none',
                visibility: 'hidden',
                height: 0,
                overflow: 'hidden',
            } },
            React.createElement("div", { className: `${prefixCls}-indent` },
                React.createElement("div", { ref: indentMeasurerRef, className: `${prefixCls}-indent-unit` }))),
        React.createElement(VirtualList, Object.assign({}, domProps, { data: mergedData, itemKey: itemKey, height: height, fullHeight: false, virtual: virtual, itemHeight: itemHeight, prefixCls: `${prefixCls}-list`, ref: listRef }), (treeNode) => {
            const { pos } = treeNode, restProps = __rest(treeNode.data, []), { title, key, isStart, isEnd } = treeNode;
            const mergedKey = getKey(key, pos);
            delete restProps.key;
            delete restProps.children;
            const treeNodeProps = getTreeNodeProps(mergedKey, treeNodeRequiredProps);
            return (React.createElement(MotionTreeNode, Object.assign({}, restProps, treeNodeProps, { title: title, active: !!activeItem && key === activeItem.data.key, pos: pos, data: treeNode.data, isStart: isStart, isEnd: isEnd, motion: motion, motionNodes: key === MOTION_KEY ? transitionRange : null, motionType: motionType, onMotionStart: onListChangeStart, onMotionEnd: onMotionEnd, treeNodeRequiredProps: treeNodeRequiredProps, onMouseMove: () => {
                    onActiveChange(null);
                } })));
        })));
};
const NodeList = React.forwardRef(RefNodeList);
NodeList.displayName = 'NodeList';
export default NodeList;
